import {
  CircleDot,
  Flag,
  Gauge,
  KeyRound,
  Link2,
  MapPin,
  MapPinned,
  Phone,
  Truck,
  UserRound,
  X,
} from "lucide-react";
import { STATE_META, fmtCoords, fmtKm, progressPct, sinceLabel, vehicleState } from "@/lib/fleet";

const Stat = ({ label, value, unit, testid, accent }) => (
  <div className="border border-white/10 bg-[#121212] px-3 py-3" data-testid={testid}>
    <p className="overline">{label}</p>
    <p className="font-head mt-1 text-2xl font-black tracking-tight" style={accent ? { color: accent } : {}}>
      {value}
      {unit && <span className="ml-1 text-[10px] font-normal text-[#52525B]">{unit}</span>}
    </p>
  </div>
);

export const VehicleDetailPanel = ({
  vehicle,
  onClose,
  onSetLocation,
  onShare,
  onSetTripStatus,
  onSetHaltPoint,
  isClient,
  supervisor,
}) => {
  const trip = vehicle.active_trip;
  const meta = STATE_META[vehicleState(vehicle)];
  const pct = progressPct(trip);

  return (
    <div className="flex h-full flex-col bg-[#0A0A0A]" data-testid="vehicle-detail-panel">
      <div className="flex items-start justify-between border-b border-white/10 px-5 py-4">
        <div>
          <p className="overline">vehicle unit</p>
          <h2 className="font-head text-xl font-black tracking-tight" data-testid="detail-plate">
            {vehicle.plate_number}
          </h2>
          <span
            className="mt-2 inline-block border px-1.5 py-0.5 text-[9px] tracking-[0.15em]"
            style={{ color: meta.color, borderColor: `${meta.color}55` }}
            data-testid="detail-status-badge"
          >
            {meta.label}
          </span>
        </div>
        <button
          data-testid="close-detail-panel-btn"
          onClick={onClose}
          className="border border-white/10 p-1.5 text-[#A1A1AA] transition-colors duration-150 hover:border-[#00F0FF] hover:text-[#00F0FF]"
          aria-label="Close panel"
        >
          <X size={14} />
        </button>
      </div>

      <div className="doot-scroll flex-1 overflow-y-auto px-5 py-5">
        <p className="overline mb-3">route</p>
        {trip ? (
          <div className="space-y-3 border-l border-white/10 pl-4" data-testid="detail-route">
            <div className="relative">
              <CircleDot size={12} className="absolute -left-[21px] top-0.5 text-[#00F0FF]" />
              <p className="text-[10px] tracking-[0.2em] text-[#52525B]">ORIGIN</p>
              <p className="text-sm">{trip.origin_name}</p>
            </div>
            <div className="relative">
              <Flag size={12} className="absolute -left-[21px] top-0.5 text-[#00FF66]" />
              <p className="text-[10px] tracking-[0.2em] text-[#52525B]">DESTINATION</p>
              <p className="text-sm">{trip.destination_name}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#52525B]" data-testid="detail-no-trip">
            NOT ASSIGNED
          </p>
        )}

        {trip?.halt && null}

        {vehicle.governance_breach && (
          <p
            className="mt-4 border border-[#FF3B30]/50 bg-[#FF3B30]/10 px-3 py-2 text-[11px] text-[#FF3B30]"
            data-testid="detail-governance-breach"
          >
            Over governed limit · {vehicle.governance_breach}
          </p>
        )}

        {trip && (
          <div className="mt-4 border border-white/10 bg-[#121212] px-3 py-3" data-testid="detail-governed-duration">
            <p className="overline">governed duration left</p>
            <p className="font-head mt-1 text-2xl font-black tracking-tight">
              {Number(trip.governed_duration_h || 0).toFixed(1)}
              <span className="ml-1 text-[10px] font-bold tracking-[0.15em] text-[#52525B]">HOURS</span>
            </p>
            <p className="mt-1 text-[10px] tracking-[0.12em] text-[#52525B]">
              {fmtKm(trip.distance_remaining_km)} KM AT GOVERNED CEILING
            </p>
          </div>
        )}

        {trip && !isClient && onSetHaltPoint && (
          <div className="mt-4 border border-white/10 bg-[#121212] px-3 py-3" data-testid="detail-halt-point">
            <p className="overline">halt point</p>
            <p className="mt-1 text-[11px]">
              {trip.halt_pos ? (
                <span className={trip.halt_applied ? "text-[#FF3B30]" : "text-[#00F0FF]"}>
                  {trip.halt_name} · {trip.halt_radius_m} m
                  {trip.halt_applied ? " · reached, parked" : ""}
                </span>
              ) : (
                <span className="text-[#52525B]">not set</span>
              )}
            </p>
            <button
              data-testid="set-halt-point-btn"
              onClick={onSetHaltPoint}
              className="mt-2 flex items-center gap-1.5 border border-white/15 px-2 py-1 text-[9px] tracking-[0.15em] text-[#A1A1AA] transition-colors duration-150 hover:border-[#00F0FF] hover:text-[#00F0FF]"
            >
              <MapPinned size={11} /> {trip.halt_pos ? "CHANGE HALT POINT" : "SET HALT POINT"}
            </button>
          </div>
        )}

        {trip && !isClient && onSetTripStatus && (
          <div className="mt-4" data-testid="detail-trip-status">
            <p className="overline mb-2">trip status</p>
            <div className="flex flex-wrap gap-2">
              {["assigned", "in_progress", "parked", "completed"].map((s) => (
                <button
                  key={s}
                  data-testid={`trip-status-${s}-btn`}
                  onClick={() => onSetTripStatus(trip.id, s)}
                  className={`border px-2 py-1 text-[9px] tracking-[0.15em] transition-colors duration-150 ${
                    trip.status === s
                      ? "border-[#00F0FF] text-[#00F0FF]"
                      : "border-white/15 text-[#A1A1AA] hover:border-white/40 hover:text-white"
                  }`}
                >
                  {s.replace("_", " ").toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}

        {trip && (
          <div className="mt-6" data-testid="detail-progress">
            <div className="flex items-end justify-between">
              <p className="overline">trip progress</p>
              <p className="text-xs text-[#00FF66]">{pct.toFixed(0)}%</p>
            </div>
            <div className="mt-2 h-[2px] w-full bg-white/10">
              <div
                className="h-full bg-[#00FF66] transition-[width] duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 gap-3">
          <Stat
            label="distance left"
            value={trip ? fmtKm(trip.distance_remaining_km) : "--"}
            unit="KM"
            accent="#00F0FF"
            testid="stat-distance-remaining"
          />
          <Stat
            label="covered"
            value={trip ? fmtKm(trip.distance_covered_km) : "--"}
            unit="KM"
            testid="stat-distance-covered"
          />
          <Stat
            label="total trip"
            value={trip ? fmtKm(trip.planned_distance_km) : "--"}
            unit="KM"
            testid="stat-planned-distance"
          />
          <Stat
            label="avg speed"
            value={trip ? Number(trip.avg_speed_kmh || 0).toFixed(0) : "--"}
            unit="KM/H"
            accent="#FFCC00"
            testid="stat-avg-speed"
          />
        </div>

        <div className="mt-3 divide-y divide-white/5 border border-white/10 bg-[#121212]">
          <Row icon={KeyRound} label="Ignition" testid="detail-ignition">
            <span style={{ color: vehicle.ignition_state === "on" ? "#00FF66" : "#FF3B30" }}>
              {vehicle.ignition_state?.toUpperCase()}
            </span>
          </Row>
          <Row icon={Gauge} label="Speed" testid="detail-speed">
            {Number(vehicle.speed_kmh || 0).toFixed(0)} KM/H
          </Row>
          <Row icon={MapPin} label="Position" testid="detail-coords">
            {fmtCoords(vehicle.current_location)}
          </Row>
        </div>

        <p className="overline mt-6 mb-2">crew</p>
        <div className="divide-y divide-white/5 border border-white/10 bg-[#121212]">
          <Row icon={UserRound} label="Driver" testid="detail-driver-name">
            {vehicle.driver_name || "UNASSIGNED"}
          </Row>
          <Row icon={Truck} label="Vehicle no." testid="detail-driver-vehicle">
            {vehicle.plate_number}
          </Row>
          <Row icon={Phone} label="Supervisor" testid="detail-contact">
            {supervisor?.supervisor_phone || "CONTACT DISPATCH"}
          </Row>
        </div>
        {isClient && (
          <p className="overline mt-2" data-testid="client-privacy-note">
            {supervisor?.supervisor_name
              ? `supervisor · ${supervisor.supervisor_name}`
              : "driver contact details are private"}
          </p>
        )}

        <p className="overline mt-5">
          last ping · {sinceLabel(vehicle.current_location_updated_at)} ago · unit {vehicle.gps_device_id || "n/a"}
        </p>

        {onSetLocation && (
          <button
            data-testid="set-location-btn"
            onClick={onSetLocation}
            className="mt-4 flex w-full items-center justify-center gap-2 border border-white/15 px-3 py-2.5 text-[10px] tracking-[0.2em] text-white transition-colors duration-150 hover:border-[#00F0FF] hover:text-[#00F0FF]"
          >
            <MapPinned size={12} /> SEARCH &amp; SET POSITION
          </button>
        )}

        {onShare && trip && (
          <button
            data-testid="share-shipment-btn"
            onClick={onShare}
            className="mt-2 flex w-full items-center justify-center gap-2 border border-[#00F0FF]/50 px-3 py-2.5 text-[10px] tracking-[0.2em] text-[#00F0FF] transition-colors duration-150 hover:bg-[#00F0FF] hover:text-black"
          >
            <Link2 size={12} /> SHARE WITH CLIENT
          </button>
        )}
      </div>
    </div>
  );
};

const Row = ({ icon: Icon, label, children, testid }) => (
  <div className="flex items-center justify-between px-3 py-2.5 text-xs" data-testid={testid}>
    <span className="flex items-center gap-2 text-[#A1A1AA]">
      <Icon size={12} className="text-[#52525B]" />
      {label}
    </span>
    <span>{children}</span>
  </div>
);
